import React, { useMemo } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useListTemplatesQuery } from '../../../store/templateLibrary/templateLibrary'
import type { TemplateLookup, TemplateScope } from '../../../models/TemplateLibrary'
import { TemplateScopeMapping } from './TemplateScopeMapping'

export interface ReplacementOption {
  id: string
  label: string
  displayLabel: React.ReactNode
  scope: TemplateScope
}

export interface UseReplacementOptionsArgs {
  templateValue: TemplateLookup
  scopes: TemplateScope[]
  createdBy?: string
  skip?: boolean
}

export interface UseReplacementOptionsResult {
  replacementOptions: ReplacementOption[] | undefined
  defaultReplacement: ReplacementOption | null
}

export const useReplacementOptions = ({ templateValue, scopes, createdBy, skip = false }: UseReplacementOptionsArgs): UseReplacementOptionsResult => {
  const accountId = useAppSelector(selectAccountId)
  const { data: templates } = useListTemplatesQuery({ accountId, scopes, type: templateValue.type, typeId: templateValue.typeId, createdBy }, { skip })

  const replacementOptions = useMemo<ReplacementOption[] | undefined>(
    () =>
      templates?.data
        .filter((template) => template.id !== templateValue.id)
        .map((template) => ({
          id: template.id,
          label: `${template.name} (${TemplateScopeMapping[template.scope].title})`,
          displayLabel: (
            <div className='zocks-py-0.125 inline-block w-auto' key={template.id}>
              <span>{template.name}</span>
              <span className='zocks-text-gray-600'> ({TemplateScopeMapping[template.scope].title})</span>
            </div>
          ),
          scope: template.scope,
        })),
    [templates?.data, templateValue.id],
  )

  const defaultReplacement = useMemo<ReplacementOption | null>(
    () => replacementOptions?.find((opt) => opt.scope === 'account') ?? replacementOptions?.find((opt) => opt.scope === 'system') ?? null,
    [replacementOptions],
  )

  return { replacementOptions, defaultReplacement }
}
